<template>
    <nav class="main-header" aria-label="primary">
        <button
            class="main-header__action"
            :class="{ 'main-header__action--active': headerStore.sidebarOpen }"
            @click="actionClick"
        >
            <div class="main-header__icon">
                <Transition name="main-header__icon">
                    <SvgIcon :name="activeIcon" :key="activeIcon" :size="24" />
                </Transition>
            </div>
            <span class="main-header__action__text">Menu</span>
        </button>
        <Sidebar v-if="menu" :menu />
        <div class="main-header__spacer"></div>
        <Transition name="main-header-heading">
            <div class="main-header__heading" v-if="isHeadingVisible">
                <h1>{{ headerStore.text }}</h1>
                <span v-if="headerStore?.subText">{{ headerStore.subText }}</span>
            </div>
            <RouterLink
                :to="PATHS.HOME"
                class="main-header__logo"
                v-else-if="headerStore?.displaySazkaBetLogo && isMobile"
            >
                <img src="/shared-img/header/sazkabet-logo.svg" alt="Logo SazkaBetu" width="94" height="40" />
            </RouterLink>
        </Transition>
        <a v-if="!isMobile" :href="logoUrl" class="main-header__logo">
            <img src="/shared-img/header/sazka-logo.svg" alt="Logo Sazky" width="40" height="40" />
        </a>
        <ul v-if="isMdLarger" class="main-header__links">
            <li>
                <a :href="`${portalUrl}/loterie`">Loterie</a>
            </li>
            <li>
                <a :href="`${portalUrl}/losy`">Losy</a>
            </li>
            <li>
                <a :href="`${portalUrl}/kurzove-sazky`">Kurzové sázky</a>
            </li>
            <li>
                <a :href="`${portalUrl}/hry`">Hry</a>
            </li>
            <li>
                <a :href="`${portalUrl}/hry-live`">Hry Live</a>
            </li>
        </ul>
        <MainHeaderUser />
    </nav>
    <Transition name="middle-header">
        <div
            v-if="isMiddleHeaderVisible"
            v-show="!isHeaderScrolled"
            class="middle-header"
            :class="{ 'middle-header--visible': isMiddleHeaderVisible && !isHeaderScrolled }"
        >
            <FavouriteMatchButton
                :name="headerStore.text"
                v-if="headerStore?.favoriteMatchId"
                :id="headerStore.favoriteMatchId"
            />
            <div class="main-header__heading">
                <h1>{{ headerStore.text }}</h1>
                <span v-if="headerStore?.subText">{{ headerStore.subText }}</span>
            </div>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useWindowScroll } from "@vueuse/core";
import { useRouter } from "vue-router";
import Sidebar from "@/header/components/organisms/Sidebar.vue";
import FavouriteMatchButton from "@/shared/components/atoms/FavouriteMatchButton.vue";
import MainHeaderUser from "@/header/components/molecules/MainHeaderUser.vue";
import { useHeader } from "@/shared/stores/use-header";
import useIsBreakpoint from "@/shared/composables/use-is-breakpoint";
import { PATHS } from "@/router/paths";
import { Menu } from "@/api/interfaces/menu";
import SvgIcon from "@/shared/components/atoms/SvgIcon.vue";
import analytics from "@/shared/analytics";

defineProps<{
    menu: Menu;
}>();

const { y: scrollY } = useWindowScroll();
const router = useRouter();
const { isMobile, isMdLarger } = useIsBreakpoint();

const portalUrl = environment.portalBaseUrl;

const headerStore = useHeader();

const isMiddleHeaderVisible = computed(() => headerStore?.text && headerStore?.showMiddleText && isMobile.value);
const isHeaderScrolled = computed(() => scrollY.value > 50 && isMiddleHeaderVisible.value);
const isHeadingVisible = computed(() => {
    return headerStore?.text && isMobile.value && (!headerStore?.showMiddleText || isHeaderScrolled.value);
});

const activeIcon = computed(() => {
    if (isMobile.value) {
        if (headerStore.closeIcon || headerStore.sidebarOpen) {
            return "close-big";
        }
        if (headerStore?.backLink && router.currentRoute.value.path !== PATHS.PREMATCH) {
            return "arrow-left";
        }
    } else {
        return headerStore.sidebarOpen ? "close-big" : "menu";
    }
    return "menu";
});

const actionClick = () => {
    if (headerStore?.backLink && isMobile.value) {
        if (headerStore.backLink === "$ROUTER_BACK$") {
            const currentPath = router.currentRoute.value.path;
            const previousPath = document.referrer.replace(window.location.origin, "");

            // Check if the previous path is the same as the current path
            if (previousPath === currentPath) {
                // Handle the cycle by navigating to a fallback route or a parent route
                const parentPath = currentPath.split("/").slice(0, -1).join("/");
                router.push({ path: parentPath || PATHS.HOME });
            } else {
                router.back();
            }
        } else if (headerStore.backLink) {
            // If the backlink is the same as router.back -> use router.back()
            // so we have access to the savedPosition and can scroll user to it
            if (headerStore?.backLink === router.options.history.state.back) router.back();
            else router.push({ path: headerStore.backLink });
        } else {
            router.push({ path: PATHS.HOME });
        }
    } else {
        headerStore.sidebarOpen = !headerStore.sidebarOpen;
        if (isMobile.value) {
            analytics.sendSideMenuOpenImpression();
        }
    }
};

const logoUrl = environment.portalBaseUrl;
</script>
